<template>
  <div
    class="fixed top-0 left-0 z-50 w-full flex items-center justify-center h-screen bg-black bg-opacity-25"
  >
    <div
      class="w-2/3 h-2/3 overflow-y-auto relative bg-white rounded-xl shadow-xl"
    >
      <!-- Header -->
      <div class="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center">
        <div class="min-w-0">
          <h2 class="text-lg font-semibold text-gray-900 truncate">{{ customer.company }}</h2>
        </div>
        <div class="flex items-center gap-2 ml-auto flex-shrink-0">
          <router-link
            :to="{name: 'ClientDocs', query: {customerId: customer.id}}"
            class="inline-flex items-center px-4 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors whitespace-nowrap"
            style="height:36px"
          >
            <i class="fa-solid fa-arrow-up-right-from-square mr-1.5"></i>View Client
          </router-link>
          <button
            @click="toggleView()"
            class="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Projects List -->
      <div v-if="!showAddProject" class="px-6 py-5">
        <!-- Toolbar -->
        <div class="flex flex-wrap items-center gap-3 mb-4">
          <h3 class="text-sm font-semibold text-gray-700">Projects ({{ filteredProjects.length }})</h3>

          <div class="inline-flex items-center bg-white border border-gray-200 rounded-lg" style="height:36px;width:220px">
            <i class="fa-solid fa-magnifying-glass text-gray-400 text-xs pl-3 flex-shrink-0"></i>
            <input
              v-model="searchQuery"
              placeholder="Search projects..."
              class="bg-transparent text-sm pl-2 pr-3 outline-none w-full h-full"
            />
          </div>

          <div class="flex items-center gap-2 ml-auto">
            <button
              @click="showClosed = !showClosed"
              class="inline-flex items-center px-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
              :class="showClosed
                ? 'bg-primary text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'"
              style="height:36px"
            >
              <i class="fa-solid fa-eye mr-1.5" v-if="showClosed"></i>
              <i class="fa-solid fa-eye-slash mr-1.5" v-else></i>
              Closed
            </button>
            <button
              @click="showAddProject = true"
              class="inline-flex items-center px-3 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors whitespace-nowrap"
              style="height:36px"
            >
              <i class="fa-solid fa-plus mr-1.5"></i>Add Project
            </button>
          </div>
        </div>

        <!-- Project cards -->
        <div class="space-y-2">
          <div
            v-for="(p, index) in filteredProjects"
            :key="'project' + index + p.id"
            class="flex items-center gap-2"
          >
            <div class="flex-1 min-w-0">
              <project-tile :project="p" />
            </div>
            <button
              @click="duplicateProject(p)"
              class="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title="Duplicate project"
            >
              <i class="fa-solid fa-copy text-sm"></i>
            </button>
          </div>
          <div v-if="!filteredProjects.length" class="py-12 text-center text-sm text-gray-400">
            No projects found
          </div>
        </div>
      </div>

      <!-- Add Project Form -->
      <div v-if="showAddProject" class="px-6 py-5">
        <div class="flex items-center mb-5">
          <h3 class="text-sm font-semibold text-gray-700">Create Project</h3>
          <button
            @click="showAddProject = false"
            class="ml-auto inline-flex items-center px-3 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors"
            style="height:36px"
          >
            <i class="fa-solid fa-arrow-left mr-1.5"></i>Back
          </button>
        </div>

        <div class="bg-gray-50 border border-gray-200 rounded-xl p-6">
          <div class="grid grid-cols-2 gap-4">
            <!-- Client -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Client</label>
              <select
                v-model="client"
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              >
                <option :value="c" v-for="(c, index) in clients" :key="index">{{ c }}</option>
              </select>
            </div>

            <!-- Call Frequency -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Call Frequency</label>
              <select
                v-model="callFrequency"
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              >
                <option value="daily">Daily</option>
                <option value="never">Don't Call</option>
              </select>
            </div>

            <!-- Project ID -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Project ID</label>
              <input
                :disabled="true"
                type="number"
                v-model="projectId"
                :placeholder="latestJob.projectId + 1"
                class="w-full bg-gray-100 border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-500"
                style="height:40px"
              />
            </div>

            <!-- Project Name -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Project Name</label>
              <input
                type="text"
                placeholder="The Pavillion"
                v-model="projectName"
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              />
            </div>

            <!-- PO Number -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">PO Number</label>
              <input
                type="text"
                placeholder="QC X3"
                v-model="poNumber"
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              />
            </div>

            <!-- Contact -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Contact</label>
              <input
                type="text"
                placeholder="Frank Junior"
                v-model="contact"
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              />
            </div>

            <!-- Phone -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Phone</label>
              <input
                type="text"
                placeholder="+61431000000"
                v-model="phone"
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              />
            </div>

            <!-- Email -->
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Email</label>
              <input
                type="text"
                placeholder="frank@test.com"
                v-model="email"
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              />
            </div>

            <!-- Address (full width) -->
            <div class="col-span-2 relative">
              <label class="block text-xs font-medium text-gray-500 mb-1">Address</label>
              <input
                type="text"
                v-model="addressSearchQuery"
                @input="debounceAddressSearch()"
                placeholder="Start typing an address..."
                class="w-full bg-white border border-gray-200 rounded-lg text-sm px-3 outline-none text-gray-700"
                style="height:40px"
              />
              <div v-if="addressSearchResults.length > 0" class="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <div v-for="(result, idx) in addressSearchResults" :key="idx" @click="selectAddressResult(result)" class="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                  {{ result.place_name }}
                </div>
              </div>
            </div>

            <!-- Submit -->
            <div class="col-span-2 pt-2">
              <button
                @click="submitForm(true)"
                class="inline-flex items-center px-6 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-colors"
                style="height:40px"
              >
                <i class="fa-solid fa-plus mr-1.5"></i>Create Project Job
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { setGeoPoint } from "../../firebase";
const geofire = require("geofire-common");
import { getJobsByCustomerId, getClients, getLatestJob, postFire, setFire } from "../../firestore/utils";
import { getDocs, query, collection, orderBy, limit } from "firebase/firestore";
import { bus } from "../../main";
import ProjectTile from "@/components/cards/ProjectTile";
import { auth, db } from "../../firebase";

const MAPBOX_TOKEN = "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";
import { onAuthStateChanged } from "firebase/auth";
export default {
  props: ["customer"],
  data() {
    return {
      searchQuery: '',
      address: '',
      email: '',
      phone: '',
      contact: '',
      poNumber: '',
      latestJob: null,
      client: '',
      projectId: '',
      projectName: '',
      callFrequency: 'Daily',
      clients:[],
      projects: [],
      showClosed: false,
      showAddProject: false,
      addressFound: false,
      overrideAddress: "",
      geoHash: null,
      geoHashPoint: null,
      addressObj: null,
      addressSearchQuery: "",
      addressSearchResults: [],
      addressGeoTimer: null,
    };
  },
  computed:{
    filteredProjects() {
    let filtered = this.projects;

    if (!this.showClosed) {
      filtered = filtered.filter(project => project.active);
    }

    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      filtered = filtered.filter(project =>
        (project.projectName && project.projectName.toLowerCase().includes(q)) ||
        (project.projectAddress && project.projectAddress.toLowerCase().includes(q)) ||
        (project.projectId && project.projectId.toString().includes(q))
      );
    }

    return filtered;
  }
  },
  methods: {
    async initiate() {
      const allJobs = await getJobsByCustomerId(this.customer.id);
      this.clients = await getClients();
      this.getTheLatestJob()
      console.log("allJobs", allJobs);
      this.mapJobsToProject(allJobs);
    },
    debounceAddressSearch() {
      if (this.addressGeoTimer) clearTimeout(this.addressGeoTimer);
      this.addressGeoTimer = setTimeout(() => {
        this.geocodeAddressSearch();
      }, 300);
    },
    async geocodeAddressSearch() {
      const q = (this.addressSearchQuery || '').trim();
      if (q.length < 3) {
        this.addressSearchResults = [];
        return;
      }
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=au&limit=5`;
        const res = await fetch(url);
        const data = await res.json();
        this.addressSearchResults = data.features || [];
      } catch (e) {
        console.error('Geocode error:', e);
        this.addressSearchResults = [];
      }
    },
    selectAddressResult(result) {
      this.addressSearchQuery = result.place_name;
      this.overrideAddress = result.place_name;
      this.addressFound = false;
      if (result.center) {
        this._selectedLat = result.center[1];
        this._selectedLng = result.center[0];
      }
      this.addressSearchResults = [];
    },
    async getTheLatestJob(){
      this.latestJob = await getLatestJob()
      if(!this.latestJob) this.latestJob = {projectId: 0}
      this.projectId = this.latestJob.projectId + 1
      console.log("latestJob", this.latestJob)
    },
    toggleView() {
      bus.$emit("closeClientCard");
    },
    mapJobsToProject(jobs) {
      jobs.map((i) => {
        const exists = this.projects.filter((x) => x.projectId == i.projectId);
        console.log("exists", exists);
        if (exists.length > 0)
          this.projects[this.projects.indexOf(exists[0])].jobs.push(i);
        else {
          this.projects.push({
            id: i.id,
            projectId: i.projectId,
            projectName: i.projectName,
            projectAddress: i.formattedAddress,
            contact: i.contact,
            phone: i.phone,
            email: i.email,
            active: i.active,
            jobs: [i],
          });
        }
      });
    },
    async submitForm(_redirect) {

      if(this.projectId <= this.latestJob.projectId) alert(`Project Id must be larger than the lastest project which is ${this.latestJob.projectId}`)
      if (
        this.projectId &&
        this.projectName &&
        this.contact &&
        this.phone &&
        this.poNumber &&
        this.email
      ) {
        try {

          this.loading = true;
          let geohash = this.geoHash;
          let geopoint = this.geoPoint;
          let _address = this.addressObj
          let _formattedAddress = ''

          if(!this.addressFound){
            _formattedAddress = this.overrideAddress
            _address = null
            geohash = ''
            geopoint = null
          }

          if (this.addressFound) {
            _formattedAddress = this.formatAddress(this.address)
            const lat = this.address.latitude;
            const lng = this.address.longitude;
            _address = this.address
            geohash = geofire.geohashForLocation([lat, lng]);
            geopoint = setGeoPoint({
              latitude: this.address.latitude,
              longitude: this.address.longitude,
            });
          }

          // Create the project document in the projects collection
          const projectPayload = {
            projectId: this.projectId,
            projectName: this.projectName,
            company: this.customer.company,
            customerId: this.customer.id,
            contact: this.contact,
            phone: this.phone,
            email: this.email,
            formattedAddress: _formattedAddress,
            callFrequency: this.callFrequency,
            poNumber: this.poNumber,
            active: true,
            closed: false,
            created: new Date(),
          };

          await setFire({
            collection: "projects",
            doc: String(this.projectId),
            body: projectPayload,
          });

          // Create the initial job for this project
          let jobPayload = {
            created: new Date(),
            formattedAddress: _formattedAddress,
            client: this.client,
            active: true,
            company: this.customer.company,
            phone: this.phone,
            email: this.email,
            callFrequency: this.callFrequency,
            contact: this.contact,
            poNumber: this.poNumber,
            customerId: this.customer.id,
            address: _address,
            projectId: this.projectId,
            projectName: this.projectName,
            geohash,
            geopoint,
            status: 'ON-CALL',
            contractor: null,
            meters: null,
            mixCode: '',
            published: false,
            requirements: '',
            notes: '',
            updated: new Date()
          };

          const a = await postFire({
            collection: "jobs",
            body: jobPayload,
          });

          const _user = await this.getCurrentUser();

          await postFire({
            collection: "customerPosts",
            body: {
              postedBy: _user.uid,
              type: "jobCreated",
              body: `A job was updated for ${this.projectName} at ${_formattedAddress}.`,
              projectId: this.projectId,
              customerId: this.customer.id,
              created: new Date(),
            },
          });

          // Add the new project to the local list so it appears reactively
          this.projects.push({
            id: a.id,
            projectId: this.projectId,
            projectName: this.projectName,
            projectAddress: _formattedAddress,
            contact: this.contact,
            phone: this.phone,
            email: this.email,
            active: true,
            jobs: [{ ...jobPayload, id: a.id }],
          });

          // Reset form fields
          this.projectName = "";
          this.projectId = this.latestJob.projectId + 1;
          this.status = "";
          this.callFrequency = "";
          this.contact = "";
          this.poNumber = "";
          this.phone = "";
          this.email = "";
          this.search = "";
          this.address = "";
          this.addressSearchQuery = "";
          this.addressSearchResults = [];
          this.editMode = null;

          this.showAddProject = false;

          // Navigate to the jobs page for the new job
          this.$router.push({ name: 'Jobs', query: { jobId: a.id } });
          this.toggleView();

        } catch (e) {
          alert("Something went wrong");
          console.log("Error", e);
        }
      } else {
        alert("Required fields - Project Id, ProjectName, Contact, Phone, Email");
      }
    },

    async getCurrentUser() {
        return new Promise((resolve, reject) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            user ? resolve(user) : reject(new Error("No user is signed in."));
          });
        });
      },

    async duplicateProject(project) {
      try {
        // Find max projectId across projects collection
        const projSnap = await getDocs(query(collection(db, "projects"), orderBy("projectId", "desc"), limit(1)));
        let maxId = 0;
        projSnap.forEach(d => {
          const id = parseInt(d.data().projectId, 10);
          if (!isNaN(id) && id > maxId) maxId = id;
        });
        // Also check jobs collection for legacy data
        if (this.latestJob && this.latestJob.projectId > maxId) {
          maxId = this.latestJob.projectId;
        }
        const newProjectId = maxId + 1;

        const payload = {
          projectId: newProjectId,
          projectName: (project.projectName || '') + ' (Copy)',
          company: this.customer.company || '',
          contact: project.contact || '',
          phone: project.phone || '',
          email: project.email || '',
          formattedAddress: project.projectAddress || project.formattedAddress || '',
          region: project.region || '',
          poNumber: project.poNumber || '',
          secondaryContact: project.secondaryContact || '',
          secondaryPhone: project.secondaryPhone || '',
          secondaryEmail: project.secondaryEmail || '',
          callFrequency: project.callFrequency || '',
          customerId: this.customer.id || '',
          active: true,
          closed: false,
          created: new Date(),
        };

        await setFire({
          collection: "projects",
          doc: String(newProjectId),
          body: payload,
        });

        this.$router.push({ name: 'Jobs', query: { projectId: String(newProjectId), editProject: 'true' } });
      } catch (e) {
        console.error("Failed to duplicate project:", e);
        alert("Failed to duplicate project");
      }
    },

    formatAddress(address) {
      if (this.addressFound) {
        if (address && address.route) {
          return `${address.street_number} ${address.route} ${address.locality}, ${address.administrative_area_level_1} ${address.country} ${address.postal_code}`;
        } else {
          return null;
        }
      }
    },
  },
  created() {
    this.initiate();
  },
  components: {
    ProjectTile,
  },
};
</script>
